import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, Ticket, ChevronLeft, ChevronRight, Search, Download } from 'lucide-react';
import eventsService from '../services/eventsService';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';

/** Legacy events may have ticket tiers in eventData but isTicketed=false (dashboard used to omit flags). */
function hasTicketTiersInEventData(event) {
  const data = event.eventData;
  if (!data || typeof data !== 'object') return false;
  return Array.isArray(data.ticketTiers) && data.ticketTiers.length > 0;
}

function isBookablePublicEvent(event) {
  const ticketed = Boolean(event.isTicketed) || hasTicketTiersInEventData(event);
  return (
    ticketed &&
    event.isPublic !== false &&
    event.isActive !== false
  );
}

function getEffectiveTicketPrice(event) {
  if (event.ticketPrice != null && Number(event.ticketPrice) > 0) {
    return Number(event.ticketPrice);
  }
  const tiers = event.eventData?.ticketTiers;
  if (Array.isArray(tiers) && tiers.length > 0) {
    const prices = tiers.map((t) => Number(t.price)).filter((n) => !Number.isNaN(n) && n > 0);
    if (prices.length) return Math.min(...prices);
  }
  return 0;
}

const EventsCalendar = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { customer } = useCustomerAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const fetchedEvents = await eventsService.getEvents({ limit: 100 });
      
      // Filter for upcoming public bookable events (ticketed and/or has ticketTiers in eventData)
      const now = new Date();
      const upcomingEvents = fetchedEvents.filter(event => {
        if (!event.date) return false;
        const eventDate = new Date(event.date);
        if (Number.isNaN(eventDate.getTime())) return false;
        return isBookablePublicEvent(event) && eventDate >= now;
      });
      
      setEvents(upcomingEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const openEventModal = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const closeEventModal = () => {
    setSelectedEvent(null);
    setShowEventModal(false);
  };

  const handleBookEvent = async (event) => {
    if (!customer) {
      // Redirect to login
      navigate('/customer/login', { state: { returnTo: '/events-calendar' } });
      return;
    }

    try {
      // Add event as a product to cart (if events are sold as products)
      // Or create ticket directly

      // For now, we'll add it to cart as a product
      // You may need to create an event product or handle this differently
      const price = getEffectiveTicketPrice(event);
      if (price <= 0) {
        alert('This event has no valid ticket price. Please contact the organiser.');
        return;
      }

      const eventProduct = {
        id: `event-${event.id}`,
        eventId: event.id,
        name: event.name,
        title: event.name,
        price,
        type: 'event',
        image: event.coverImage,
      };

      addToCart(eventProduct);
      closeEventModal();
      
      // Navigate to cart
      navigate('/cart');
    } catch (error) {
      console.error('Error booking event:', error);
      alert('Failed to book event. Please try again.');
    }
  };

  const filteredEvents = events.filter(event => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return event.name.toLowerCase().includes(query) ||
           (event.description && event.description.toLowerCase().includes(query)) ||
           (event.location && event.location.toLowerCase().includes(query));
  });

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = getDaysInMonth(currentDate);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a8a, #7c3aed, #ec4899)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid rgba(255,255,255,0.3)',
            borderTop: '3px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p>Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at center, #0c1445 0%, #1a1a2e 50%, #16213e 100%)',
      padding: '100px 20px 40px',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto 40px',
        textAlign: 'center'
      }}>
        <h1 style={{
          fontSize: '64px',
          fontWeight: 'bold',
          color: 'white',
          margin: '0 0 20px 0',
          textShadow: '3px 3px 6px rgba(0,0,0,0.5)',
          background: 'linear-gradient(135deg, #fbbf24, #f59e0b, #d97706)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Book Events
        </h1>
        
        <p style={{
          fontSize: '20px',
          color: '#e5e7eb',
          marginBottom: '30px',
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
        }}>
          Browse and book upcoming classes, training sessions, and events
        </p>

        {/* Search and View Toggle */}
        <div style={{
          display: 'flex',
          gap: '20px',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: '30px'
        }}>
          <div style={{
            position: 'relative',
            maxWidth: '400px',
            width: '100%'
          }}>
            <Search 
              size={20} 
              style={{
                position: 'absolute',
                left: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }}
            />
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px 12px 50px',
                borderRadius: '50px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                color: 'white',
                fontSize: '16px',
                outline: 'none'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: '10px',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '50px',
            padding: '4px'
          }}>
            <button
              onClick={() => setViewMode('month')}
              style={{
                padding: '10px 20px',
                borderRadius: '50px',
                border: 'none',
                background: viewMode === 'month' 
                  ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' 
                  : 'transparent',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Calendar
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '10px 20px',
                borderRadius: '50px',
                border: 'none',
                background: viewMode === 'list' 
                  ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' 
                  : 'transparent',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              List
            </button>
          </div>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'month' && (
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '30px',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          {/* Month Navigation */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px'
          }}>
            <button
              onClick={() => navigateMonth(-1)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <ChevronLeft size={24} />
            </button>

            <h2 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: 'white',
              margin: 0
            }}>
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>

            <button
              onClick={() => navigateMonth(1)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Calendar Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: '10px',
            width: '100%'
          }}>
            {/* Day Headers */}
            {dayNames.map(day => (
              <div
                key={day}
                style={{
                  padding: '15px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#fbbf24',
                  fontSize: '14px',
                  minWidth: 0
                }}
              >
                {day}
              </div>
            ))}

            {/* Calendar Days */}
            {days.map((date, index) => {
              const dayEvents = getEventsForDate(date);
              const isToday = date && date.toDateString() === new Date().toDateString();
              
              return (
                <div
                  key={index}
                  onClick={() => date && dayEvents.length > 0 && openEventModal(dayEvents[0])}
                  style={{
                    minHeight: '100px',
                    minWidth: 0,
                    maxWidth: '100%',
                    padding: '8px',
                    overflow: 'hidden',
                    background: date 
                      ? isToday 
                        ? 'rgba(251, 191, 36, 0.2)' 
                        : 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    borderRadius: '8px',
                    border: date && isToday ? '2px solid #fbbf24' : '1px solid rgba(255, 255, 255, 0.1)',
                    cursor: date && dayEvents.length > 0 ? 'pointer' : 'default',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    boxSizing: 'border-box'
                  }}
                  onMouseEnter={(e) => {
                    if (date && dayEvents.length > 0) {
                      e.currentTarget.style.background = 'rgba(251, 191, 36, 0.3)';
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (date) {
                      e.currentTarget.style.background = isToday 
                        ? 'rgba(251, 191, 36, 0.2)' 
                        : 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  {date && (
                    <>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: isToday ? 'bold' : 'normal',
                        color: isToday ? '#fbbf24' : 'white',
                        marginBottom: '4px'
                      }}>
                        {date.getDate()}
                      </div>
                      
                      {dayEvents.slice(0, 2).map((event) => (
                        <div
                          key={event.id}
                          title={typeof event.name === 'string' ? event.name : ''}
                          style={{
                            fontSize: '11px',
                            padding: '4px 6px',
                            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                            borderRadius: '4px',
                            color: 'white',
                            marginBottom: '4px',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: '500',
                            minWidth: 0
                          }}
                        >
                          {typeof event.name === 'string' ? event.name : String(event.name ?? '')}
                        </div>
                      ))}
                      
                      {dayEvents.length > 2 && (
                        <div style={{
                          fontSize: '11px',
                          color: '#fbbf24',
                          fontWeight: '500'
                        }}>
                          +{dayEvents.length - 2} more
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {filteredEvents.length === 0 ? (
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              padding: '60px',
              textAlign: 'center',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <h2 style={{ color: 'white', fontSize: '24px', marginBottom: '10px' }}>
                No upcoming events
              </h2>
              <p style={{ color: '#9ca3af', fontSize: '16px' }}>
                Check back later for new events!
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '24px'
            }}>
              {filteredEvents.map(event => {
                const eventDate = new Date(event.date);
                const ticketsSold = event._count?.tickets || 0;
                const availableTickets = event.maxCapacity 
                  ? Math.max(0, event.maxCapacity - ticketsSold)
                  : 'Unlimited';
                const isSoldOut = event.maxCapacity && ticketsSold >= event.maxCapacity;

                return (
                  <div
                    key={event.id}
                    onClick={() => openEventModal(event)}
                    style={{
                      background: 'rgba(0, 0, 0, 0.8)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 12px 40px rgba(251, 191, 36, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
                    }}
                  >
                    {/* Cover Image */}
                    {event.coverImage && (
                      <div style={{
                        width: '100%',
                        height: '200px',
                        overflow: 'hidden'
                      }}>
                        <img
                          src={event.coverImage}
                          alt={event.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                    )}

                    {/* Event Info */}
                    <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <h3 style={{
                        fontSize: '1.5rem',
                        fontWeight: '600',
                        color: 'white',
                        marginBottom: '12px',
                        lineHeight: '1.3'
                      }}>
                        {event.name}
                      </h3>

                      {event.description && (
                        <p style={{
                          color: '#9ca3af',
                          fontSize: '14px',
                          lineHeight: '1.5',
                          marginBottom: '16px',
                          flex: 1
                        }}>
                          {event.description.length > 100 
                            ? event.description.substring(0, 100) + '...' 
                            : event.description}
                        </p>
                      )}

                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        marginBottom: '16px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fbbf24' }}>
                          <Calendar size={16} />
                          <span style={{ fontSize: '14px' }}>
                            {eventDate.toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </span>
                        </div>

                        {event.location && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#e5e7eb' }}>
                            <MapPin size={16} />
                            <span style={{ fontSize: '14px' }}>{event.location}</span>
                          </div>
                        )}

                        {event.maxCapacity && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#e5e7eb' }}>
                            <Users size={16} />
                            <span style={{ fontSize: '14px' }}>
                              {availableTickets} of {event.maxCapacity} tickets available
                            </span>
                          </div>
                        )}

                        {getEffectiveTicketPrice(event) > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#e5e7eb' }}>
                            <Ticket size={16} />
                            <span style={{ fontSize: '14px', fontWeight: '600' }}>
                              ${getEffectiveTicketPrice(event).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBookEvent(event);
                        }}
                        disabled={isSoldOut}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          border: 'none',
                          background: isSoldOut
                            ? 'rgba(107, 114, 128, 0.5)'
                            : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                          color: 'white',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: isSoldOut ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSoldOut) {
                            e.currentTarget.style.transform = 'scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(251, 191, 36, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSoldOut) {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {isSoldOut ? 'Sold Out' : 'Book Now'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Event Detail Modal */}
      {showEventModal && selectedEvent && (
        <div
          onClick={closeEventModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '600px',
              width: '100%',
              background: 'rgba(30, 30, 30, 0.95)',
              borderRadius: '20px',
              padding: '30px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            {selectedEvent.coverImage && (
              <img
                src={selectedEvent.coverImage}
                alt={selectedEvent.name}
                style={{
                  width: '100%',
                  height: '300px',
                  objectFit: 'cover',
                  borderRadius: '12px',
                  marginBottom: '20px'
                }}
              />
            )}

            <h2 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '20px'
            }}>
              {selectedEvent.name}
            </h2>

            {selectedEvent.description && (
              <p style={{
                color: '#e5e7eb',
                fontSize: '16px',
                lineHeight: '1.6',
                marginBottom: '20px'
              }}>
                {selectedEvent.description}
              </p>
            )}

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginBottom: '30px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#fbbf24' }}>
                <Calendar size={20} />
                <span style={{ fontSize: '16px' }}>
                  {new Date(selectedEvent.date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>

              {selectedEvent.endDate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#e5e7eb' }}>
                  <Clock size={20} />
                  <span style={{ fontSize: '16px' }}>
                    Ends: {new Date(selectedEvent.endDate).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              )}

              {selectedEvent.location && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#e5e7eb' }}>
                  <MapPin size={20} />
                  <span style={{ fontSize: '16px' }}>{selectedEvent.location}</span>
                </div>
              )}

              {selectedEvent.virtualUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#e5e7eb' }}>
                  <span style={{ fontSize: '16px' }}>
                    Virtual Event: <a href={selectedEvent.virtualUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#fbbf24' }}>Join Online</a>
                  </span>
                </div>
              )}

              {selectedEvent.digitalFileUrl &&
                String(selectedEvent.digitalFileUrl).trim().length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#a7f3d0' }}>
                  <Download size={20} aria-hidden />
                  <a
                    href={String(selectedEvent.digitalFileUrl).trim()}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#6ee7b7', fontWeight: 600, fontSize: '16px' }}
                  >
                    Free download (map, flyer, or bonus file)
                  </a>
                </div>
              )}

              {selectedEvent.maxCapacity && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#e5e7eb' }}>
                  <Users size={20} />
                  <span style={{ fontSize: '16px' }}>
                    Capacity: {selectedEvent.maxCapacity} attendees
                  </span>
                </div>
              )}

              {getEffectiveTicketPrice(selectedEvent) > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#e5e7eb' }}>
                  <Ticket size={20} />
                  <span style={{ fontSize: '18px', fontWeight: '600', color: '#fbbf24' }}>
                    ${getEffectiveTicketPrice(selectedEvent).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <button
              onClick={() => handleBookEvent(selectedEvent)}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                color: 'white',
                fontSize: '18px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(251, 191, 36, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Book Event
            </button>

            <button
              onClick={closeEventModal}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(0, 0, 0, 0.7)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default EventsCalendar;

